import React, { useEffect } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import {
  AuthProvider,
  type AuthProviderProps,
  useAuth,
} from 'react-oidc-context';
import { oidcConfig } from './authConfig';
import { restartAuthentication } from './authSessionRecovery';
import {
  mapNativeUrlToAppUrl,
  replaceBrowserLocation,
  runtimeConfig,
} from './platform/runtime';
import {
  closeStripeHostedCheckout,
  isStripeCheckoutReturnPath,
} from './platform/stripeCheckout';

const SessionLossRedirector: React.FC = () => {
  const auth = useAuth();

  useEffect(() => {
    const handleSessionLost = () => {
      void restartAuthentication(auth).catch(() => undefined);
    };

    const removeAccessTokenExpired = auth.events.addAccessTokenExpired(handleSessionLost);
    const removeUserSignedOut = auth.events.addUserSignedOut(handleSessionLost);

    return () => {
      removeAccessTokenExpired();
      removeUserSignedOut();
    };
  }, [auth]);

  return null;
};

const OidcProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useEffect(() => {
    if (!runtimeConfig.isNativePlatform) {
      return undefined;
    }

    let disposed = false;
    let listener: { remove: () => Promise<void> } | undefined;
    let lastHandledUrl: string | undefined;

    const handleAppUrl = async (url: string) => {
      if (disposed || url === lastHandledUrl) {
        return;
      }

      const appUrl = mapNativeUrlToAppUrl(url);

      if (!appUrl) {
        return;
      }

      lastHandledUrl = url;

      if (isStripeCheckoutReturnPath(new URL(appUrl).pathname)) {
        await closeStripeHostedCheckout();
      }

      if (!disposed) {
        replaceBrowserLocation(appUrl);
      }
    };

    CapacitorApp.addListener('appUrlOpen', ({ url }) => {
      void handleAppUrl(url);
    }).then(async handle => {
      if (disposed) {
        void handle.remove();
        return;
      }

      listener = handle;

      const launch = await CapacitorApp.getLaunchUrl();
      if (launch?.url) {
        await handleAppUrl(launch.url);
      }
    });

    return () => {
      disposed = true;
      if (listener) {
        void listener.remove();
      }
    };
  }, []);

  const onSigninCallback: AuthProviderProps['onSigninCallback'] = () => undefined;

  return (
    <AuthProvider {...oidcConfig} onSigninCallback={onSigninCallback}>
      <SessionLossRedirector />
      {children}
    </AuthProvider>
  );
};

export default OidcProvider;
